import React, { useState } from 'react';
import FadeIn from './FadeIn';

const gold = '#C8A96E';
const charcoal = '#1A1916';
const secondary = '#6B6862';

const SectionRule = () => (
  <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 72px' }} />
);

const Label = ({ children }) => (
  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>{children}</p>
);

const InfinityPage = () => {
  const [expandedVertical, setExpandedVertical] = useState(null);

  const verticals = [
    { id: 'development', name: 'Infinity Development', sub: 'Built Environment / Living Sector', desc: 'Sustainable multifamily and mixed-use development across US and LATAM markets — serving as the execution engine of the platform.' },
    { id: 'partners', name: 'Infinity Partners', sub: 'Capital Platform', desc: 'A long-horizon investment platform focused on real assets, infrastructure, and strategic capital deployment — designed to align institutional capital with disciplined execution.' },
    { id: 'nexus', name: 'Infinity Nexus', sub: 'Data & Infrastructure', desc: 'Focused on digital infrastructure, data center systems, and intelligence layers that support both the built environment and broader platform operations.' },
    { id: 'energy', name: 'Infinity Energy', sub: 'Energy & Sustainability Systems', desc: 'Exploring renewable energy, energy efficiency, and integrated infrastructure solutions that support resilient, future-proof developments.' },
    { id: 'ocean', name: 'Infinity Ocean', sub: 'Ocean & Environmental Systems', desc: 'Focused on long-term environmental stewardship, ocean innovation, and regenerative systems tied to global sustainability challenges.' },
    { id: 'space', name: 'Infinity Space', sub: 'Frontier Innovation', desc: 'Long-term exploration of space, aerospace systems, and off-world infrastructure — aligned with the broader mission of extending human capability.' },
    { id: 'aero', name: 'Infinity Aero', sub: 'Aviation', desc: 'Comprehensive aviation platform spanning private jets, commercial mobility, manufacturing, and next-gen propulsion — driving connectivity and synergies.' },
    { id: 'lunar', name: 'Infinity Lunar', sub: 'Lunar Infrastructure', desc: 'Development of lunar habitats, resource utilization, and off-world outposts — pioneering sustainable presence on the Moon to advance human frontiers.' },
    { id: 'beyond', name: 'Infinity Beyond', sub: 'Transcendent Exploration', desc: "Pushing boundaries into interstellar travel, advanced propulsion, and cosmic infrastructure — envisioning humanity's unlimited expansion across the universe." },
    { id: 'gives', name: 'Infinity Gives', sub: 'Philanthropy & Global Impact', desc: 'A platform dedicated to deploying capital, technology, and partnerships toward solving large-scale global challenges.' },
  ];

  const phases = [
    { num: '01', label: 'Execution', desc: 'Establish Infinity Development as a disciplined, high-performing platform.' },
    { num: '02', label: 'Expansion', desc: 'Launch adjacent verticals (capital, data, energy) that directly support and enhance the core platform.' },
    { num: '03', label: 'Integration', desc: 'Create synergies across verticals — aligning capital, infrastructure, and intelligence into a unified system.' },
    { num: '04', label: 'Frontier', desc: 'Expand into long-horizon initiatives across ocean systems, advanced infrastructure, and space.' },
  ];

  const cortexModules = [
    { name: 'Originate', desc: 'Deal sourcing and underwriting system', detail: 'Structured screening + Infinity Feasibility Index (IFI)' },
    { name: 'Design', desc: 'Carbon and performance governance', detail: 'Lifecycle modeling + certification embedded early' },
    { name: 'Deliver', desc: 'Construction decision system', detail: 'Method-agnostic evaluation across cost, time, carbon, and risk' },
    { name: 'Operate', desc: 'Performance intelligence', detail: 'Real-time monitoring + investor transparency' },
    { name: 'Expand', desc: 'Platform scaling system', detail: 'Geographic growth + capital alignment + cross-border strategy' },
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="px-6 text-center" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Label>Platform</Label>
        <h1 className="font-light" style={{ color: charcoal, fontSize: 'clamp(3.5rem, 10vw, 6.5rem)', lineHeight: '1.05', letterSpacing: '-0.025em', marginBottom: '28px' }}>
          Infinity
        </h1>
        <p className="font-light" style={{ color: secondary, fontSize: '1rem', lineHeight: '1.9', maxWidth: '560px', margin: '0 auto 16px' }}>
          A long-horizon platform for building across real assets, capital, and frontier innovation.
        </p>
        <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.85', maxWidth: '480px', margin: '0 auto 16px' }}>
          Designed to operate across decades — not cycles — Infinity is a systems-driven platform focused on deploying capital, technology, and infrastructure to create durable value at scale.
        </p>
        <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.85', maxWidth: '480px', margin: '0 auto 16px' }}>
          Rooted in the convergence of real estate, energy, data, and emerging technologies, Infinity is not a single company — it is an evolving ecosystem.
        </p>
        <p className="font-light text-sm" style={{ color: charcoal, lineHeight: '1.85', maxWidth: '480px', margin: '0 auto 28px', fontStyle: 'italic' }}>
          Built deliberately. Governed institutionally. Designed to endure.
        </p>
        <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto' }} />
        <div className="scroll-arrow" style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3v14M3 10l7 7 7-7" stroke={gold} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* ─── SECTION 1 — CORE THESIS ──────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>Core Thesis</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              A Platform, Not a Company
            </h2>
            {[
              'Infinity is designed as a multi-venture platform — a structured ecosystem of interconnected verticals operating across the built environment, capital markets, and frontier innovation.',
              'At its core, Infinity is built on a simple principle: The most valuable platforms of the future will not operate in silos — they will integrate capital, infrastructure, and intelligence into unified systems.',
              'Rather than pursuing isolated opportunities, Infinity is designed to compound value across verticals — aligning development, capital, technology, and long-term stewardship into a cohesive framework.',
            ].map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 2 — EXECUTION ENGINE ────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>Execution Engine</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              Where It Starts: Infinity Development
            </h2>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>
              Every platform needs an execution ground.
            </p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>
              Infinity Development serves as the foundation — a real estate development engine focused on sustainable, high-performance living-sector assets across the United States and Latin America. Through disciplined capital structuring, carbon-conscious design, and the Infinity Cortex framework, it transforms development into a repeatable, data-driven process.
            </p>
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginTop: '32px' }}>
              {['This is where ideas are tested.', 'Where systems are refined.', 'And where the foundation of the broader platform is built.'].map((line, i) => (
                <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2.2', marginBottom: '4px' }}>{line}</p>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 3 — CORTEX ──────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <div style={{ maxWidth: '560px' }}>
              <Label>Internal Operating System</Label>
              <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}>
                Infinity Cortex
              </h2>
              <p className="font-light text-sm" style={{ color: gold, lineHeight: '1.8', marginBottom: '32px', letterSpacing: '0.04em' }}>
                The internal operating system behind Infinity Development.
              </p>
              {[
                'At the center of Infinity Development is Infinity Cortex — a proprietary internal framework that governs how projects are sourced, evaluated, designed, delivered, and operated.',
                'Cortex is not a product and is not externally distributed. It is the system that defines how decisions are made, how risk is measured, and how execution is standardized across the platform.',
              ].map((para, i) => (
                <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
              ))}
            </div>

            <div className="mt-12 mb-12" style={{ maxWidth: '560px' }}>
              <p className="font-medium text-xs uppercase mb-4" style={{ color: charcoal, letterSpacing: '0.2em' }}>Why It Exists</p>
              <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2' }}>
                Real estate development is typically fragmented and operator-dependent. Cortex was built to replace that fragmentation with a structured, repeatable, and auditable system that ensures consistency across every stage of development.
              </p>
            </div>

            <div className="mb-16">
              <p className="font-medium text-xs uppercase mb-8" style={{ color: charcoal, letterSpacing: '0.2em' }}>Decision Architecture</p>
              <p className="font-light text-sm mb-8" style={{ color: secondary, lineHeight: '2' }}>
                Every opportunity within Infinity Development is evaluated through two parallel outputs:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-8" style={{ border: `1px solid #E5E3DE`, backgroundColor: '#FAFAF8' }}>
                  <p className="font-medium text-xs uppercase mb-1" style={{ color: gold, letterSpacing: '0.18em' }}>Deal Score</p>
                  <p className="font-light text-xs mb-6" style={{ color: secondary }}>Upside — measures potential</p>
                  {['Density and yield potential', 'Revenue and rent assumptions', 'Construction feasibility', 'Market positioning'].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 mb-3">
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: gold, marginTop: '8px', flexShrink: 0 }} />
                      <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.7' }}>{item}</p>
                    </div>
                  ))}
                </div>
                <div className="p-8" style={{ border: `1px solid #E5E3DE`, backgroundColor: '#FAFAF8' }}>
                  <p className="font-medium text-xs uppercase mb-1" style={{ color: charcoal, letterSpacing: '0.18em' }}>Risk Score</p>
                  <p className="font-light text-xs mb-6" style={{ color: secondary }}>Downside — measures where it can break</p>
                  {['Entitlement risk', 'Engineering complexity', 'Capital exposure', 'Timeline uncertainty'].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 mb-3">
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: charcoal, marginTop: '8px', flexShrink: 0 }} />
                      <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.7' }}>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
              <p className="font-light text-sm mt-6" style={{ color: secondary, lineHeight: '2', fontStyle: 'italic' }}>
                Every decision is made at the intersection of these two forces.
              </p>
            </div>

            <div className="mb-16">
              <p className="font-medium text-xs uppercase mb-8" style={{ color: charcoal, letterSpacing: '0.2em' }}>Cortex Modules</p>
              <div className="space-y-px" style={{ border: `1px solid #E5E3DE` }}>
                {cortexModules.map((mod, i) => (
                  <div key={i} className="flex items-start gap-6 p-6" style={{ borderBottom: i < cortexModules.length - 1 ? `1px solid #E5E3DE` : 'none', backgroundColor: '#FAFAF8' }}>
                    <div style={{ minWidth: '20px' }}>
                      <span className="font-light text-xs" style={{ color: gold }}>0{i + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-xs uppercase mb-1" style={{ color: charcoal, letterSpacing: '0.14em' }}>{mod.name}</p>
                      <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.7' }}>{mod.desc}</p>
                      <p className="font-light text-xs mt-1" style={{ color: gold }}>{mod.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="font-light text-sm mt-6" style={{ color: secondary, lineHeight: '2', fontStyle: 'italic' }}>
                These modules are not standalone tools — they are integrated components of a single operating system.
              </p>
            </div>

            <div className="mb-12" style={{ maxWidth: '560px' }}>
              <p className="font-medium text-xs uppercase mb-6" style={{ color: charcoal, letterSpacing: '0.2em' }}>Market & Risk Intelligence</p>
              <p className="font-light text-sm mb-6" style={{ color: secondary, lineHeight: '2' }}>
                Cortex is fundamentally a market and risk intelligence system. It exists to answer three questions:
              </p>
              <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginBottom: '32px' }}>
                {['Where is the opportunity?', 'Where can it break?', 'How early can we detect it?'].map((q, i) => (
                  <p key={i} className="font-light text-sm" style={{ color: charcoal, lineHeight: '2.4' }}>— {q}</p>
                ))}
              </div>
              <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>
                By structuring development around these questions, Infinity is designed to reduce discretionary risk, accelerate approvals and execution timelines, improve capital alignment, and create projects that attract private capital earlier.
              </p>
              <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', fontStyle: 'italic' }}>
                In markets where entitlement complexity, engineering constraints, and approval timelines determine feasibility, the ability to pre-resolve risk is what allows projects to move from concept to construction.
              </p>
            </div>

            <div style={{ maxWidth: '560px' }}>
              <p className="font-medium text-xs uppercase mb-6" style={{ color: charcoal, letterSpacing: '0.2em' }}>The Data Flywheel</p>
              <p className="font-light text-sm mb-6" style={{ color: secondary, lineHeight: '2' }}>Each project strengthens the system:</p>
              {['Design assumptions improve with real construction data', 'Construction outcomes refine cost and timeline models', 'Operational performance improves future underwriting'].map((item, i) => (
                <div key={i} className="flex items-start gap-3 mb-4">
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: gold, marginTop: '8px', flexShrink: 0 }} />
                  <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.85' }}>{item}</p>
                </div>
              ))}
              <p className="font-light text-sm mt-6" style={{ color: charcoal, lineHeight: '2' }}>
                Over time, Cortex becomes a compounding intelligence advantage that cannot be replicated by new entrants.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 4 — ECOSYSTEM ───────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <Label>Ecosystem</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}>
              The Infinity Ecosystem
            </h2>
            <p className="font-light text-sm mb-12" style={{ color: secondary, lineHeight: '2', maxWidth: '540px' }}>
              Infinity evolves through a series of interconnected verticals — each operating independently, but governed by shared principles of innovation, sustainability, and long-term value creation.
            </p>
            <div style={{ border: `1px solid #E5E3DE` }}>
              {verticals.map((v, i) => {
                const open = expandedVertical === v.id;
                return (
                  <div
                    key={v.id}
                    className="cursor-pointer"
                    style={{
                      borderBottom: i < verticals.length - 1 ? `1px solid #E5E3DE` : 'none',
                      backgroundColor: open ? '#FAFAF8' : '#fff',
                      transition: 'background-color 0.25s',
                    }}
                    onClick={() => setExpandedVertical(open ? null : v.id)}
                  >
                    <div className="flex items-start justify-between gap-4 p-6">
                      <div className="flex items-start gap-5">
                        <span className="font-light text-xs mt-0.5" style={{ color: gold, minWidth: '24px' }}>0{i + 1}</span>
                        <div>
                          <p className="font-medium text-sm uppercase mb-1" style={{ color: charcoal, letterSpacing: '0.1em' }}>{v.name}</p>
                          <p className="font-light text-xs" style={{ color: gold, letterSpacing: '0.06em' }}>{v.sub}</p>
                        </div>
                      </div>
                      {/* Icon: thin circle with +/× morph */}
                      <div
                        className="flex-shrink-0 mt-0.5"
                        style={{
                          width: '22px', height: '22px',
                          border: `1px solid ${open ? gold : '#D4D0CA'}`,
                          borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'border-color 0.25s, transform 0.35s cubic-bezier(0.4,0,0.2,1)',
                          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
                          color: open ? gold : secondary,
                        }}
                      >
                        <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                          <line x1="4.5" y1="0.5" x2="4.5" y2="8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                          <line x1="0.5" y1="4.5" x2="8.5" y2="4.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </div>
                    {/* Smooth height animation */}
                    <div style={{
                      display: 'grid',
                      gridTemplateRows: open ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.35s cubic-bezier(0.4,0,0.2,1)',
                    }}>
                      <div style={{ overflow: 'hidden' }}>
                        <div className="px-6 pb-6" style={{ paddingLeft: 'calc(1.5rem + 24px + 20px)' }}>
                          <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2' }}>{v.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 5 — PHASES ──────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <Label>Approach</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}>
              Built Through Phases, Not Promises
            </h2>
            <p className="font-light text-sm mb-12" style={{ color: secondary, lineHeight: '2', maxWidth: '540px' }}>
              Infinity is not being built all at once. It is being developed deliberately — through a phased approach:
            </p>
            <div className="grid sm:grid-cols-2 gap-px" style={{ border: `1px solid #E5E3DE` }}>
              {phases.map((phase, i) => (
                <div key={i} className="p-8" style={{ backgroundColor: '#FAFAF8', borderRight: i % 2 === 0 ? `1px solid #E5E3DE` : 'none', borderBottom: i < 2 ? `1px solid #E5E3DE` : 'none' }}>
                  <p className="font-light mb-3" style={{ color: gold, fontSize: '0.7rem', letterSpacing: '0.22em' }}>Phase {phase.num}</p>
                  <p className="font-medium text-sm uppercase mb-3" style={{ color: charcoal, letterSpacing: '0.1em' }}>{phase.label}</p>
                  <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.85' }}>{phase.desc}</p>
                </div>
              ))}
            </div>
            <p className="font-light text-sm mt-8" style={{ color: secondary, lineHeight: '2', fontStyle: 'italic' }}>
              Each phase builds on the last. Each vertical is earned — not assumed.
            </p>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 6 — PHILOSOPHY ──────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>Philosophy</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              Stewardship Over Scale
            </h2>
            {[
              'This is not about building companies for the sake of scale. It is about building systems that endure.',
              'Deploying capital responsibly. Designing infrastructure intelligently. And aligning innovation with long-term human outcomes.',
            ].map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginTop: '32px' }}>
              <p className="font-light text-sm" style={{ color: charcoal, lineHeight: '2' }}>
                The goal is not short-term growth. It is multi-decade value creation across markets, systems, and generations.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 7 — CLOSING ─────────────────────────────── */}
      <section className="py-20 px-6 text-center">
        <FadeIn>
          <div style={{ maxWidth: '448px', margin: '0 auto' }}>
            <Label>Looking Forward</Label>
            {['Infinity is being built patiently.', 'Methodically.', 'With institutional discipline.', 'One vertical at a time.'].map((line, i) => (
              <p key={i} className="font-light" style={{ color: charcoal, fontSize: '1rem', lineHeight: '2', marginBottom: '4px' }}>{line}</p>
            ))}
            <p className="font-light text-sm mt-8" style={{ color: secondary, lineHeight: '2', fontStyle: 'italic' }}>
              For those who think in decades, not quarters — this is where alignment begins.
            </p>
            <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '40px auto 0' }} />
          </div>
        </FadeIn>
      </section>

    </div>
  );
};

export default InfinityPage;
